import type { Contract, Task } from '../services/api';
import { sumTasksNumericField } from './taskFinance';

export type BudgetGroupId = 'cost' | 'margin';

export const BUDGET_GROUP_LABELS: Record<BudgetGroupId, string> = {
  cost: 'РАСХОДЫ',
  margin: 'ПРИБЫЛЬ',
};

export const BUDGET_EXTRA_EXPENSES_KEY = 'extra_expenses';

const BUDGET_ROW_TASK_FIELD: Record<string, keyof Task> = {
  amount: 'amount',
  planned_cost: 'planned_cost',
  taxes: 'taxes',
  service_fee: 'service_fee',
  partner_commission: 'partner_commission',
  planned_margin: 'planned_margin',
  adjustment: 'adjustment',
};

export interface MarginFinanceRow {
  key: string;
  label: string;
  draftKey?: keyof Task;
  visible: boolean;
  budgetGroup?: BudgetGroupId;
  computed?: boolean;
}

export interface HeaderMergeCell {
  value: string;
  colSpan: number;
}

export interface MarginCalcRowSum {
  row: MarginFinanceRow;
  valuesByTaskId: Map<number, number | null>;
  rowSum: number;
}

export interface MarginCalcGroupSum {
  valuesByTaskId: Map<number, number | null>;
  rowSum: number;
}

export interface MarginCalcTableData {
  rowSums: MarginCalcRowSum[];
  amountRowSum: number;
  groupSums: Record<BudgetGroupId, MarginCalcGroupSum>;
}

export function buildMarginFinanceRows(params: {
  canSum: boolean;
  canCost: boolean;
  canMargin: boolean;
}): MarginFinanceRow[] {
  const { canSum, canCost, canMargin } = params;
  const rows: MarginFinanceRow[] = [
    {
      key: 'planned_cost',
      label: 'Себестоимость',
      draftKey: 'planned_cost',
      visible: canCost,
      budgetGroup: 'cost',
    },
    {
      key: 'taxes',
      label: 'Налог',
      draftKey: 'taxes',
      visible: canCost,
      budgetGroup: 'cost',
    },
    {
      key: 'service_fee',
      label: 'Сервисный сбор',
      draftKey: 'service_fee',
      visible: canCost,
      budgetGroup: 'cost',
    },
    {
      key: 'partner_commission',
      label: 'Комиссия партнёра',
      draftKey: 'partner_commission',
      visible: canMargin,
      budgetGroup: 'cost',
    },
    {
      key: BUDGET_EXTRA_EXPENSES_KEY,
      label: 'Доп. расходы',
      visible: canSum,
      computed: true,
      budgetGroup: 'cost',
    },
    {
      key: 'planned_margin',
      label: 'Маржа',
      draftKey: 'planned_margin',
      visible: canMargin,
      budgetGroup: 'margin',
    },
    {
      key: 'adjustment',
      label: 'Корректировка стоимости',
      draftKey: 'adjustment',
      visible: canMargin,
      budgetGroup: 'margin',
    },
    {
      key: 'amount',
      label: 'Сумма',
      draftKey: 'amount',
      visible: canSum,
    },
  ];
  return rows.filter((row) => row.visible);
}

export function mergeConsecutiveHeaderCells(values: string[]): HeaderMergeCell[] {
  const cells: HeaderMergeCell[] = [];
  let index = 0;
  while (index < values.length) {
    let end = index + 1;
    while (end < values.length && values[end] === values[index]) {
      end += 1;
    }
    cells.push({ value: values[index], colSpan: end - index });
    index = end;
  }
  return cells;
}

export function sortMarginCalcColumns(
  rows: Task[],
  contractsById: Map<number, Contract>,
  workTypeLabel: (id: number | null | undefined) => string,
): Task[] {
  return [...rows].sort((left, right) => {
    const leftContract =
      contractsById.get(left.contract_id)?.contract_number ?? String(left.contract_id);
    const rightContract =
      contractsById.get(right.contract_id)?.contract_number ?? String(right.contract_id);
    const byContract = leftContract.localeCompare(rightContract, 'ru', {
      numeric: true,
      sensitivity: 'base',
    });
    if (byContract !== 0) return byContract;
    const byTaskId = left.task_id.localeCompare(right.task_id, 'ru');
    if (byTaskId !== 0) return byTaskId;
    return workTypeLabel(left.work_type_id).localeCompare(workTypeLabel(right.work_type_id), 'ru');
  });
}

export function taskMarginFieldValue(rowKey: string, taskRow: Task): number | null {
  const field = BUDGET_ROW_TASK_FIELD[rowKey];
  if (!field) return null;
  const raw = taskRow[field];
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

export function buildMarginCalcHeaderCells(
  columnTasks: Task[],
  contractsById: Map<number, Contract>,
  workTypeLabel: (id: number | null | undefined) => string,
): {
  contracts: HeaderMergeCell[];
  taskIds: HeaderMergeCell[];
  workTypes: HeaderMergeCell[];
} {
  const contractNumbers = columnTasks.map(
    (column) =>
      contractsById.get(column.contract_id)?.contract_number ??
      column.contract_number ??
      '—',
  );
  const taskIds = columnTasks.map((column) => column.task_id);
  const workTypes = columnTasks.map((column) => workTypeLabel(column.work_type_id));
  return {
    contracts: mergeConsecutiveHeaderCells(contractNumbers),
    taskIds: mergeConsecutiveHeaderCells(taskIds),
    workTypes: mergeConsecutiveHeaderCells(workTypes),
  };
}

export function buildMarginCalcTableData(params: {
  financeRows: MarginFinanceRow[];
  columnTasks: Task[];
  valueForColumn: (rowKey: string, columnTask: Task) => number | null;
}): MarginCalcTableData {
  const { financeRows, columnTasks, valueForColumn } = params;

  const rowSums = financeRows.map((row) => {
    const valuesByTaskId = new Map<number, number | null>();
    let rowSum = 0;
    for (const columnTask of columnTasks) {
      const value = valueForColumn(row.key, columnTask);
      valuesByTaskId.set(columnTask.contracts_task_id, value);
      rowSum += value ?? 0;
    }
    return { row, valuesByTaskId, rowSum };
  });

  const amountRowSum = rowSums.find((item) => item.row.key === 'amount')?.rowSum ?? 0;

  const sumRows = (keys: string[]): MarginCalcGroupSum => {
    const valuesByTaskId = new Map<number, number | null>();
    let rowSum = 0;
    for (const columnTask of columnTasks) {
      let columnSum = 0;
      for (const key of keys) {
        const item = rowSums.find((entry) => entry.row.key === key);
        columnSum += item?.valuesByTaskId.get(columnTask.contracts_task_id) ?? 0;
      }
      valuesByTaskId.set(columnTask.contracts_task_id, columnSum);
      rowSum += columnSum;
    }
    return { valuesByTaskId, rowSum };
  };

  const costRowKeys = rowSums
    .filter((item) => item.row.budgetGroup === 'cost')
    .map((item) => item.row.key);
  const marginRowKeys = rowSums
    .filter((item) => item.row.budgetGroup === 'margin')
    .map((item) => item.row.key);

  return {
    rowSums,
    amountRowSum,
    groupSums: {
      cost: sumRows(costRowKeys),
      margin: sumRows(marginRowKeys),
    },
  };
}

export function sumTasksPlannedExpenses(tasks: Task[]): number | null {
  const amountSum = sumTasksNumericField(tasks, 'amount');
  if (amountSum == null) return null;
  const marginSum = sumTasksNumericField(tasks, 'planned_margin') ?? 0;
  const adjustmentSum = sumTasksNumericField(tasks, 'adjustment') ?? 0;
  return amountSum - marginSum - adjustmentSum;
}

export function buildReadOnlyMarginValueGetter(financeRows: MarginFinanceRow[]) {
  const otherBudgetRowKeys = financeRows
    .filter((row) => row.key !== 'amount' && !row.computed && row.draftKey)
    .map((row) => row.key);

  return (rowKey: string, columnTask: Task): number | null => {
    if (rowKey === BUDGET_EXTRA_EXPENSES_KEY) {
      const amount = taskMarginFieldValue('amount', columnTask);
      if (amount == null) return null;
      const othersSum = otherBudgetRowKeys.reduce(
        (sum, key) => sum + (taskMarginFieldValue(key, columnTask) ?? 0),
        0,
      );
      return amount - othersSum;
    }
    return taskMarginFieldValue(rowKey, columnTask);
  };
}

export interface ContractTasksAmountMismatch {
  mismatch: boolean;
  delta: number;
  tasksAmountSum: number;
}

export function resolveContractTasksAmountMismatch(
  familyContractSum: number,
  familyTasks: Task[],
): ContractTasksAmountMismatch {
  const tasksAmountSum = sumTasksNumericField(familyTasks, 'amount') ?? 0;
  const delta = familyContractSum - tasksAmountSum;
  return {
    mismatch: Math.abs(delta) > 0.009,
    delta,
    tasksAmountSum,
  };
}
